import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import PricingTable from '@/components/PricingTable';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSteps from '@/components/ProcessSteps';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Scale, FileText, Shield, Award } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function TTABPetition() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    { icon: Scale, title: 'Expert Representation', description: 'Experienced attorneys to handle complex TTAB proceedings.' },
    { icon: FileText, title: 'Thorough Preparation', description: 'Comprehensive preparation of all required legal documents.' },
    { icon: Shield, title: 'Strategic Defense', description: 'Strategic approach to oppositions and cancellations.' },
    { icon: Award, title: 'Proven Track Record', description: 'Successful outcomes in TTAB proceedings.' },
  ];

  const faqs = [
    { question: 'What is the TTAB?', answer: 'The Trademark Trial and Appeal Board (TTAB) is an administrative tribunal within the USPTO that handles disputes related to trademark applications and registrations.' },
    { question: 'When would I need to file a TTAB petition?', answer: 'You may need to file with the TTAB to oppose a pending trademark application, cancel an existing registration, or appeal an examining attorney\'s refusal.' },
    { question: 'How long do TTAB proceedings take?', answer: 'TTAB proceedings can take 1-2 years or more, depending on the complexity of the case and whether it goes to trial.' },
    { question: 'Do I need an attorney for TTAB proceedings?', answer: 'While not required, TTAB proceedings are complex legal matters. Professional representation significantly increases your chances of success.' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1589829545856-d10d557cf95f?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>File TTAB Petition</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">Expert legal representation for Trademark Trial and Appeal Board proceedings.</p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why Choose Our TTAB Services?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Professional legal services for TTAB oppositions, cancellations, and appeals.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center"><Icon className="text-white" size={32} /></div></div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <ProcessSteps />

      {/* Pricing Section */}
      <PricingTable />

      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Learn more about TTAB proceedings and petitions.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <TestimonialSlider autoSlide={true} />
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Need TTAB Representation?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Protect your trademark rights with expert TTAB legal services.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="ttab_petition" />
      <Footer />
    </div>
  );
}